<template>
  <div
    id="phrase-library-table"
    data-test="phrase-library-table"
    class="panel-body"
  >
    <div class="table-responsive">
      <clip-loader v-show="isLoading" />

      <table
        class="table interactive-table"
        :class="{ 'table-hover': phrases.length > 0 }"
      >
        <thead class="sortable-table-header">
          <tr>
            <th>{{ trans("users.phraseLibrary.shortcutLabel") }}</th>
            <th>{{ trans("users.phraseLibrary.expandedTextLabel") }}</th>
            <th>{{ trans("examination.created_at") }}</th>
            <th>
              {{ trans("admin.platformMessages.actions") }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-if="phrases.length === 0"
            data-test="phrase-library-empty-row"
            class="special-row"
          >
            <td colspan="4" class="text-center tw-cursor-default">
              <div style="margin-top: 1.5rem">
                {{ trans("users.phraseLibrary.emptyListMessage") }}
              </div>
            </td>
          </tr>
          <tr
            v-for="phrase in phrases"
            v-else
            :key="phrase.id"
            :data-test="`phrase-library-row-${phrase.shorthand}`"
            :class="{ 'highlight-row': phrase._highlight }"
          >
            <td>
              <span
                data-test="phrase-library-row-shortcut"
                class="text-muted"
                >{{ phrase.shorthand }}</span
              >
            </td>
            <td class="pr-2">
              <VDropdown :triggers="['hover']" class="tw-inline">
                <span
                  data-test="phrase-library-row-expanded"
                  class="text-muted phrase-expanded-text-span"
                  >{{ phrase.expanded }}
                </span>
                <template #popper>
                  <div>
                    {{ phrase.expanded }}
                  </div>
                </template>
              </VDropdown>
            </td>
            <td class="pr-2">
              <span>{{ phrase.created_at.split("T")[0] }}</span>
            </td>
            <td>
              <div class="d-flex tw-gap-4 flex-nowrap">
                <button
                  type="button"
                  data-test="phrase-library-row-edit-button"
                  class="btn btn-primary text-nowrap"
                  @click="emit('on-edit', phrase)"
                >
                  {{ trans("general.edit") }}
                </button>
                <button
                  type="button"
                  data-test="phrase-library-row-delete-button"
                  class="btn btn-danger text-nowrap"
                  @click="confirmDeletePhrase(phrase.id)"
                >
                  {{ trans("general.formDeleteLabel") }}
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import ClipLoader from "../../../../Utils/ClipLoader.vue";
import { Phrase } from "./UserPhraseLibrary.vue";
import VueSimpleAlert from "vue3-simple-alert";
import { lang } from "../../../../../i18n";

const props = defineProps<{
  phrases: Phrase[];
  isLoading: boolean;
}>();

const emit = defineEmits<{
  (e: "on-delete", id: number): void;
  (
    e: "on-edit",
    phrase: { id: number; shorthand: string; expanded: string },
  ): void;
}>();

const confirmDeletePhrase = async (phraseId: number) => {
  try {
    const confirmed = await VueSimpleAlert.confirm(
      lang.get("users.phraseLibrary.deletePhraseConfirmMessage"),
      undefined,
      undefined,
      {
        confirmButtonText: lang.get("general.modalConfirmButton"),
        cancelButtonText: lang.get("general.modalCancelButton"),
        onOpen: (popup: HTMLElement) => {
          popup
            .querySelector(".swal2-confirm")
            ?.setAttribute("data-test", "phrase-library-delete-confirm");
          popup
            .querySelector(".swal2-cancel")
            ?.setAttribute("data-test", "phrase-library-delete-cancel");
        },
      },
    );

    if (confirmed) {
      emit("on-delete", phraseId);
    }
  } catch (error: any) {
    if (error && error.message !== "cancel") {
      console.error("Delete confirmation failed:", error);
    }
  }
};
</script>

<style lang="scss" scoped>
td:first-child {
  width: 15%;
  min-width: 100px;
}

th:nth-child(2),
td:nth-child(2) {
  max-width: 45vw;
  white-space: nowrap;
  vertical-align: middle;
}

th:last-child,
td:last-child {
  width: 15%;
}

.table tbody tr.highlight-row {
  animation: highlightFade 3s ease forwards;
}

@keyframes highlightFade {
  0% {
    background-color: rgba(77, 125, 187, 0.4);
  }
  100% {
    background-color: inherit;
  }
}
</style>
